import { useEffect, useMemo, useRef, useState } from 'react'
import { Form, FormProvider, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Stack, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { debounce, get } from 'lodash'
import { v4 as uuid } from 'uuid'

import { AttendeesForm } from '@/components/Event/NewScript/components/Attendance/AttendeesForm'
import { FormErrors } from '@/components/Event/NewScript/components/common/forms/FormErrors'
import { FormWarnings } from '@/components/Event/NewScript/components/common/forms/FormWarnings'
import { ActionContainer } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionContainer'
import { ActionText } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionText'
import { ActionTitle } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionTitle'
import { ContinueButton } from '@/components/Event/NewScript/components/common/ScriptTimeline/ContinueButton'
import { ScriptTimeline } from '@/components/Event/NewScript/components/common/ScriptTimeline/ScriptTimeline'
import { TimelineAction } from '@/components/Event/NewScript/components/common/ScriptTimeline/TimelineAction'
import { useGlobalAttendeesWarnings } from '@/components/Event/NewScript/hooks/form/useGlobalAttendeesWarnings'
import {
  appendWitnessSlice,
  canRemoveWitnessSlice,
  removeUnstartedWitnessSlice,
  updateAttendees,
} from '@/components/Event/NewScript/hooks/state/stateActions'
import { useScriptFlowState } from '@/components/Event/NewScript/hooks/state/useScriptFlowState'
import { useStepActions } from '@/components/Event/NewScript/hooks/state/useStepActions'
import { useStatePolicies } from '@/components/Event/NewScript/hooks/useStatePolicy'
import { useEventZoomParticipants } from '@/components/Event/NewScript/queries/scriptData'
import {
  EVENT_FORM_CONTEXT_IS_DOCUMENT_NUMBER_REQUIRED,
  EVENT_FORM_CONTEXT_IS_WITNESS_LOCATION_REQUIRED,
  EVENT_FORM_CONTEXT_IS_ZOOM_PARTICIPANTS_MAPPING_REQUIRED,
} from '@/components/Event/NewScript/schemas'
import { presenceStepSchema } from '@/components/Event/NewScript/schemas/presenceStepSchema'
import { shouldPreserveZoomParticipants } from '@/components/Event/NewScript/utils/attendees'
import {
  getWitnessEventTitle,
  isExaminationUnderOath,
} from '@/components/Event/NewScript/utils/event'
import { formatStepKey } from '@/components/Event/NewScript/utils/stepKey'
import { AttendeeRole, ScriptStep, ScriptStepStatus } from '@/constants'
import { useModals } from '@/hooks/useModals'
import { useReporter } from '@/providers/UserProvider'
import { Event, ReporterBase, ScriptAttendee, ScriptStepProps } from '@/types'
import {
  getBookedReporterRole,
  isAdminEvent,
  isBookedReporterInAttendees,
} from '@/utils/parrot'
import { getEventTimezone } from '@/utils/time'

const withReporterAttendee = (
  attendees: ScriptAttendee[],
  reporter: ReporterBase,
  event: Pick<Event, 'reporter_silo'>,
) => {
  const reporterRole = getBookedReporterRole(event)

  return [
    ...attendees,
    {
      attendee_id: uuid(),
      name: reporter.name,
      email: reporter.email,
      role: reporterRole,
      present: null,
      transcript_order: null,
      zoom_participants: [],
      identity_document: null,
    },
  ]
}

export const canManageWitnesses = (reviewStatus: ScriptStepStatus): boolean =>
  reviewStatus === ScriptStepStatus.INCOMPLETE

export const canManageWitnessesInAttendanceStep = ({
  currentEventIndex,
  reviewStatus,
}: {
  currentEventIndex?: number
  reviewStatus: ScriptStepStatus
}): boolean =>
  currentEventIndex !== undefined && canManageWitnesses(reviewStatus)

export const AttendanceStep = ({ event }: ScriptStepProps) => {
  const { actions, state, currentEvent, currentEventIndex } =
    useScriptFlowState()
  const { canHaveCna, shouldConfirmStipulations } = useStatePolicies(event)
  const [cancellationFlowStarted, setCancellationFlowStarted] = useState(false)
  const reporter = useReporter()
  const assignedReporter = isAdminEvent(event) ? event.reporter : reporter
  const { openConfirmModal } = useModals()

  const {
    canContinue,
    completeStepAndContinue,
    completeAction,
    updateAttendeesAndCompleteAction,
    isActionCompleted,
  } = useStepActions(state.current_step, event)

  const { data: eventParticipants } = useEventZoomParticipants(event, {
    refetchInterval: 10_000,
  })

  const attendees = useMemo(() => {
    return currentEvent?.attendees ?? []
  }, [currentEvent])

  // if the event is canceled/CNA/Rescheduled or no participants we can disable the mapping
  const isZoomParticipantsMappingRequired =
    eventParticipants &&
    eventParticipants.length > 0 &&
    !cancellationFlowStarted

  const isReporterInScriptState = isBookedReporterInAttendees(attendees, event)

  const methods = useForm({
    mode: 'onSubmit', // We already validate on every change using watch subscription, so no need to use "all" mode
    resolver: yupResolver(presenceStepSchema),
    shouldFocusError: false,
    context: {
      [EVENT_FORM_CONTEXT_IS_ZOOM_PARTICIPANTS_MAPPING_REQUIRED]:
        isZoomParticipantsMappingRequired,
      [EVENT_FORM_CONTEXT_IS_DOCUMENT_NUMBER_REQUIRED]: false,
      [EVENT_FORM_CONTEXT_IS_WITNESS_LOCATION_REQUIRED]: false,
    },
    defaultValues: {
      attendees:
        assignedReporter && !isReporterInScriptState
          ? withReporterAttendee(attendees, assignedReporter, event)
          : attendees,
      zoomParticipants: eventParticipants,
    },
  })
  const hasEditedZoomParticipants = useRef(false)

  useEffect(() => {
    // Reset the form whenever current event changes
    hasEditedZoomParticipants.current = false
    methods.reset({
      attendees:
        assignedReporter && !isReporterInScriptState
          ? withReporterAttendee(attendees, assignedReporter, event)
          : attendees,
      zoomParticipants: eventParticipants,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEventIndex])

  const watchAttendees = useWatch({
    control: methods.control,
    name: 'attendees',
  })
  const canManageWitnessesForReviewStatus = canManageWitnesses(
    state.steps[ScriptStep.REVIEW]?.status ?? ScriptStepStatus.INCOMPLETE,
  )

  // TODO: improve performance, triggering validation on every change is not optimal
  //  added debounce to reduce amount of validations triggered when typing
  const validateForm = useMemo(
    () =>
      debounce(value => {
        methods.trigger().then(isValid => {
          if (isValid) {
            actions.updateAttendees({
              attendees: value.attendees,
              preserveExistingZoomParticipants:
                !hasEditedZoomParticipants.current,
            })
          }
        })
      }, 300),
    [actions, methods],
  )

  useEffect(() => {
    const sub = methods.watch((value, { name }) => {
      if (!name?.includes('attendees')) {
        return
      }
      if (!shouldPreserveZoomParticipants(name)) {
        hasEditedZoomParticipants.current = true
      }
      validateForm(value)
    })

    return () => {
      sub.unsubscribe()
      validateForm.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch, validateForm])

  useEffect(() => {
    if (!eventParticipants) {
      return
    }

    const mappedParticipants = [
      ...new Set([...attendees.flatMap(a => a.zoom_participants)]),
    ]
    const newParticipants = eventParticipants.filter(
      p => !mappedParticipants.some(mp => mp?.id === p.id),
    )

    if (newParticipants.length > 0) {
      // Keep the form's participant list fresh so late joiners can be mapped
      // even after the attendance action has been completed.
      methods.setValue('zoomParticipants', eventParticipants)

      // Only surface the notification while attendance is still being
      // collected — after completion, late arrivals appear silently in the
      // dropdown to avoid interrupting the reporter mid-script.
      if (!isActionCompleted('attendees')) {
        showNotification({
          title: 'New Zoom participant detected',
          message:
            'Please assign it to the correct attendee before continuing.',
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventParticipants])

  const witnessCnaText = useMemo(() => {
    return (
      attendees.find(a => a.role === AttendeeRole.WITNESS)?.name ?? 'witness'
    )
  }, [attendees])

  const onContinueButtonPressed = () => {
    methods.trigger().then(isValid => {
      if (isValid) {
        const nextStep = ScriptStep.OPENING

        const containsInterpreter = attendees.some(
          a => a.role === AttendeeRole.INTERPRETER,
        )

        const containsGuardian = attendees.some(
          a => a.role === AttendeeRole.GUARDIAN,
        )
        const actionsToSkip = []
        if (!containsInterpreter) {
          actionsToSkip.push({
            step: nextStep,
            key: 'identify_interpreter',
          })
        }

        if (!containsGuardian) {
          actionsToSkip.push({
            step: nextStep,
            key: 'identify_guardian',
          })
        }

        if (actionsToSkip.length > 0) {
          actions.skipStepAction({
            currentEventIndex: currentEventIndex,
            stepAndActionKeys: actionsToSkip,
          })
        }

        completeStepAndContinue(
          formatStepKey(ScriptStep.OPENING, currentEventIndex),
        )
      }
    })
  }

  const validateAndCompleteAction = (key: string) => {
    methods.trigger().then(isValid => {
      if (isValid) {
        // Use combined action to update attendees and complete in a single state mutation
        // This prevents race conditions with debounced uploads overwriting each other
        const formValues = methods.getValues()
        updateAttendeesAndCompleteAction(
          {
            attendees: formValues.attendees,
            preserveExistingZoomParticipants:
              !hasEditedZoomParticipants.current,
          },
          key,
        )
        hasEditedZoomParticipants.current = false
      }
    })
  }

  const { warnings } = useGlobalAttendeesWarnings(watchAttendees as any)
  const canManageWitnessesInThisStep = canManageWitnessesInAttendanceStep({
    currentEventIndex,
    reviewStatus:
      state.steps[ScriptStep.REVIEW]?.status ?? ScriptStepStatus.INCOMPLETE,
  })
  const disabledWitnessRemovalTooltip = canManageWitnessesInThisStep
    ? 'Witnesses can only be removed before their witness section has started.'
    : 'Witnesses can only be removed before review has started.'
  const witnessAttendeeIds = useMemo(
    () =>
      state.attendees
        .filter(attendee => attendee.role === AttendeeRole.WITNESS)
        .map(attendee => attendee.attendee_id),
    [state.attendees],
  )
  const { previousWitnessAttendeeIds, subsequentWitnessAttendeeIds } =
    useMemo(() => {
      const currentIndex = currentEventIndex ?? 0

      return state.events.reduce<{
        previousWitnessAttendeeIds: string[]
        subsequentWitnessAttendeeIds: string[]
      }>(
        (acc, scriptEvent, index) => {
          if (index === currentIndex) {
            return acc
          }

          const witness = scriptEvent.attendees.find(
            attendee => attendee.role === AttendeeRole.WITNESS,
          )
          if (!witness) {
            return acc
          }

          if (index < currentIndex) {
            return {
              ...acc,
              previousWitnessAttendeeIds: [
                ...acc.previousWitnessAttendeeIds,
                witness.attendee_id,
              ],
            }
          }

          return {
            ...acc,
            subsequentWitnessAttendeeIds: [
              ...acc.subsequentWitnessAttendeeIds,
              witness.attendee_id,
            ],
          }
        },
        {
          previousWitnessAttendeeIds: [],
          subsequentWitnessAttendeeIds: [],
        },
      )
    }, [currentEventIndex, state.events])

  const removableWitnessAttendeeIds = useMemo(
    () =>
      state.events.flatMap((scriptEvent, index) => {
        if (index === 0 || !canRemoveWitnessSlice(state, index)) {
          return []
        }

        const witness = scriptEvent.attendees.find(
          attendee => attendee.role === AttendeeRole.WITNESS,
        )
        return witness ? [witness.attendee_id] : []
      }),
    [state],
  )
  const stateWithCurrentFormAttendees = () =>
    updateAttendees(state, {
      attendees: methods.getValues().attendees,
      preserveExistingZoomParticipants: !hasEditedZoomParticipants.current,
    })

  const resetAttendanceFormFromState = (nextState: typeof state) => {
    const nextCurrentEvent =
      currentEventIndex !== undefined
        ? nextState.events[currentEventIndex]
        : undefined
    if (nextCurrentEvent === undefined) {
      return
    }

    const nextAttendees = nextCurrentEvent.attendees
    const nextIsReporterInScriptState = isBookedReporterInAttendees(
      nextAttendees,
      event,
    )

    hasEditedZoomParticipants.current = false
    methods.reset({
      attendees:
        assignedReporter && !nextIsReporterInScriptState
          ? withReporterAttendee(nextAttendees, assignedReporter, event)
          : nextAttendees,
      zoomParticipants: eventParticipants,
    })
  }

  const focusAttendeeNameAfterFormReset = (
    attendeeId: string,
    nextState: typeof state,
  ) => {
    const currentAttendees =
      currentEventIndex !== undefined
        ? nextState.events[currentEventIndex]?.attendees
        : undefined
    const attendeeIndex = currentAttendees?.findIndex(
      attendee => attendee.attendee_id === attendeeId,
    )

    if (attendeeIndex === undefined || attendeeIndex < 0) {
      return
    }

    setTimeout(() => {
      methods.setFocus(`attendees.${attendeeIndex}.name`)
    }, 0)
  }

  const addWitness = () => {
    if (!canManageWitnessesForReviewStatus) {
      return
    }

    validateForm.cancel()
    const witness = {
      attendee_id: uuid(),
      name: '',
      email: '',
      role: AttendeeRole.WITNESS,
      present: null,
      transcript_order: null,
      identity_document: null,
      zoom_participants: [],
      city: undefined,
      state: undefined,
    }
    const nextState = appendWitnessSlice(
      stateWithCurrentFormAttendees(),
      witness,
      {
        canHaveCna,
        isExaminationUnderOath: isExaminationUnderOath(event),
        shouldConfirmStipulations,
      },
    )
    actions.updateGlobalState(nextState)
    resetAttendanceFormFromState(nextState)
    focusAttendeeNameAfterFormReset(witness.attendee_id, nextState)
    showNotification({
      message: 'A new witness section was added.',
    })
  }

  const confirmRemoveWitness = (witnessIndex: number) => {
    if (!canManageWitnessesForReviewStatus) {
      return
    }

    openConfirmModal({
      title: 'Remove witness?',
      onConfirm: () => {
        validateForm.cancel()
        const nextState = removeUnstartedWitnessSlice(
          stateWithCurrentFormAttendees(),
          witnessIndex,
        )
        actions.updateGlobalState(nextState)
        resetAttendanceFormFromState(nextState)
      },
      children: (
        <Text size="sm">
          This attendee is listed as a witness for this event. Are you sure you
          want to remove this witness?
        </Text>
      ),
      labels: {
        cancel: 'Cancel',
        confirm: 'Remove witness',
      },
    })
  }

  const removeWitnessAttendee = (attendeeId: string) => {
    const witnessIndex = state.events.findIndex(scriptEvent =>
      scriptEvent.attendees.some(
        attendee =>
          attendee.role === AttendeeRole.WITNESS &&
          attendee.attendee_id === attendeeId,
      ),
    )

    if (witnessIndex > 0 && canRemoveWitnessSlice(state, witnessIndex)) {
      confirmRemoveWitness(witnessIndex)
    }
  }

  const globalErrors = useMemo(() => {
    const errors = methods.formState.errors
    const result: Record<string, any> = {}

    const attendeeErrors = get(errors, 'attendees.global')
    const zoomParticipantErrors = get(errors, 'zoomParticipants.message')
    const combinedErrors = { ...attendeeErrors, ...zoomParticipantErrors }

    if (combinedErrors) {
      Object.keys(combinedErrors).forEach(key => {
        result[key] = combinedErrors[key]
      })
    }

    return result
  }, [methods.formState.errors])

  const watchWitness = watchAttendees.find(a => a.role === AttendeeRole.WITNESS)
  const isMultiWitness = state.events.length > 1
  const title = isMultiWitness
    ? `${(currentEventIndex ?? 0) + 2}. Attendance - ${getWitnessEventTitle(event, watchWitness?.name)}`
    : '2. Attendance'
  return (
    <FormProvider {...methods}>
      <Form>
        <Stack align="flex-start" spacing="xl">
          <ScriptTimeline title={title}>
            <TimelineAction
              actionKey="attendees"
              title="Collect information for each attendee"
              onComplete={validateAndCompleteAction}
              completed={isActionCompleted('attendees')}
            >
              {/* Introduction */}
              <ActionContainer outer>
                <Stack>
                  <ActionText mono>
                    Hi {'{participant name}'}, I’m {reporter?.name ?? '[ ]'} and
                    I’m the Depositions by Filevine digital reporter for today.
                    Just to let you know, everything in the main room is being
                    recorded for transcription purposes. However, breakout rooms
                    are not recorded, so I’d be happy to place you in a breakout
                    room at any time when we are off the record.
                  </ActionText>
                  <ActionContainer>
                    <ActionTitle>Reminder</ActionTitle>
                    <ActionText>
                      While doing presence, check the attendees’ Zoom
                      video/audio quality and try to resolve issues that arise.
                    </ActionText>
                  </ActionContainer>
                </Stack>
              </ActionContainer>

              {/* Form */}
              <ActionContainer outer>
                <Stack>
                  <ActionText mono>
                    While we’re waiting, can you state your full name and share
                    your role? And to send you post-event documents we need your
                    email address. Can you put your full name and email in the
                    Zoom chat?
                  </ActionText>
                  <Stack>
                    <AttendeesForm
                      eventAttendees={event.attendees}
                      zoomParticipants={eventParticipants ?? []}
                      timezone={getEventTimezone(event)}
                      eventType={event.type}
                      witnessAttendeeIds={witnessAttendeeIds}
                      previousWitnessAttendeeIds={previousWitnessAttendeeIds}
                      subsequentWitnessAttendeeIds={
                        subsequentWitnessAttendeeIds
                      }
                      disabledWitnessRemovalTooltip={
                        disabledWitnessRemovalTooltip
                      }
                      removableWitnessAttendeeIds={
                        canManageWitnessesInThisStep
                          ? removableWitnessAttendeeIds
                          : []
                      }
                      onRemoveWitnessAttendee={
                        canManageWitnessesInThisStep
                          ? removeWitnessAttendee
                          : undefined
                      }
                      onAddWitness={
                        canManageWitnessesInThisStep ? addWitness : undefined
                      }
                    />
                    <FormWarnings warnings={warnings} />
                    <FormErrors errors={globalErrors} />
                  </Stack>
                  <ActionContainer>
                    <ActionTitle>Reminder</ActionTitle>
                    <ActionText>
                      Pre-filled names, roles, and emails may contain mistakes.
                      Review and correct everything. Remove words that aren’t
                      actual names (esq, office, interpreter etc.). Add all
                      missing attendees and confirm their presence.
                    </ActionText>
                  </ActionContainer>
                </Stack>
              </ActionContainer>

              {/* Waiting for the rest */}
              <ActionContainer outer>
                <Stack>
                  <ActionText mono>
                    Thank you, please let me know if I can assist with anything
                    while we wait for everyone else to arrive.
                  </ActionText>
                  <ActionText mono>
                    For the witness: to ensure a smooth transition to the
                    record, if a photo ID will be used for verification today,
                    please have that ready. Alternatively, counsel may stipulate
                    to the identity of the witness if they prefer.
                  </ActionText>
                </Stack>
              </ActionContainer>

              <Text fw={600} color="indigo.7" mt="md">
                If requested, create Zoom breakout rooms and assign attendee to
                correct one
              </Text>

              <ActionContainer outer>
                <Stack>
                  <ActionText mono>
                    As you decided for breakout room, there should be a button
                    asking you to join the room now. I’ll be here if you need
                    anything, you can leave the breakout room any time and
                    you’ll be placed back here.
                  </ActionText>
                  <ActionContainer>
                    <ActionTitle>Reminder</ActionTitle>
                    <ActionText>
                      Create at least two Zoom breakout rooms, one for
                      defendant’s side and one for plaintiff’s side.
                    </ActionText>
                  </ActionContainer>
                </Stack>
              </ActionContainer>
            </TimelineAction>

            {canHaveCna && (
              <TimelineAction
                actionKey="cna"
                title="If witness has not appeared yet, ask about witness wait time."
                onComplete={completeAction}
                completed={isActionCompleted('cna')}
              >
                <ActionContainer outer>
                  <Stack>
                    <ActionText mono>
                      How long would you like to wait for {witnessCnaText} to
                      appear? And at that time, would you like me to issue a
                      CNA?
                    </ActionText>
                  </Stack>
                </ActionContainer>
              </TimelineAction>
            )}
            <ContinueButton
              event={event}
              label="Continue to Opening"
              canContinue={canContinue()}
              canCancel={methods.formState.isValid}
              onCancelSwitchClick={(cancellationFlowStarted: boolean) => {
                setCancellationFlowStarted(cancellationFlowStarted)
                // trigger validation in next render cycle when schema context is already updated
                setTimeout(methods.trigger, 0)
              }}
              step={ScriptStep.ATTENDANCE}
              handleClick={onContinueButtonPressed}
            />
          </ScriptTimeline>
        </Stack>
      </Form>
    </FormProvider>
  )
}
